import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiYoutube } from "react-icons/fi";
import api from "../utils/api";
import Loader from "../components/Loader";

const CATEGORIES = [
  "Product Review",
  "Pooja / Ceremony",
  "Testimonial",
  "Tutorial",
  "Astrology Session",
  "Other",
];

const emptyForm = {
  title: "",
  description: "",
  category: "Product Review",
  url: "",
  featured: false,
};

export default function VideoForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/videos/${id}`)
        .then((res) => {
          // Show the canonical watch URL back to the admin
          setForm({ ...emptyForm, ...res.data, url: `https://www.youtube.com/watch?v=${res.data.youtubeId}` });
        })
        .catch(() => toast.error("Failed to load video"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    if (!form.url) {
      toast.error("Please paste a YouTube link");
      return;
    }
    setSaving(true);
    try {
      // Send as `url` — the backend extracts the video id (forgiving of any format)
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        url: form.url,
        featured: form.featured,
      };
      if (isEdit) {
        await api.put(`/videos/${id}`, payload);
        toast.success("Video updated");
      } else {
        await api.post("/videos", payload);
        toast.success("Video added");
      }
      navigate("/admin/videos");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  // Live thumbnail preview from the pasted link
  const ytId = extractId(form.url);
  const previewThumb = ytId ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` : null;

  return (
    <div className="max-w-2xl">
      <Link
        to="/admin/videos"
        className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <FiArrowLeft /> Back to Videos
      </Link>

      <h1 className="font-heading text-2xl text-maroon-800 mb-1">
        {isEdit ? "Edit Video" : "Add New Video"}
      </h1>
      <p className="text-sm text-gray-500 mb-6">Paste any YouTube link — the thumbnail is generated automatically.</p>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Link *</label>
          <div className="relative">
            <FiYoutube className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" size={18} />
            <input
              type="text"
              name="url"
              value={form.url}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>
          {form.url && !previewThumb && (
            <p className="text-xs text-red-500 mt-1">Couldn't read a video ID from that link — please check it.</p>
          )}
        </div>

        {/* Live thumbnail preview */}
        {previewThumb && (
          <div className="rounded-xl overflow-hidden border border-gray-200 relative aspect-video bg-gold-50">
            <img src={previewThumb} alt="Video preview" className="w-full h-full object-cover" />
            <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">Preview</span>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="e.g. Rudraksha Mala — Authentic Product Review"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="Short description shown under the video..."
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="w-4 h-4" />
          Featured (shown on homepage "Videos & Reviews" section)
        </label>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-maroon-700 hover:bg-maroon-800 disabled:opacity-60 text-gold-100 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <FiSave /> {saving ? "Saving..." : "Save Video"}
        </button>
      </form>
    </div>
  );
}

/** Lightweight duplicate of the backend extractor, for live preview only. */
function extractId(input) {
  if (!input) return null;
  const s = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const short = s.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
  if (short) return short[1];
  const watch = s.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (watch) return watch[1];
  const path = s.match(/(?:embed|shorts|live)\/([A-Za-z0-9_-]{11})/);
  if (path) return path[1];
  return null;
}
